// src/DepartmentForm.js

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Row, Col, Alert, Table } from 'react-bootstrap';

const DepartmentForm = () => {
  const [departmentCode, setDepartmentCode] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [description, setDescription] = useState('');
  const [departments, setDepartments] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isTableVisible, setIsTableVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');

  const handleAlertClose = () => setAlertMessage('');

  // Fetch departments
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/departments`);
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      } else {
        throw new Error('Failed to fetch departments');
      }
    } catch (error) {
      setAlertType('danger');
      setAlertMessage('An unexpected error occurred. Please try again.');
    }
  }, []);

  // Handle Create
  const handleCreate = async () => {
    const departmentData = {
      deptId: departmentCode,
      deptName: departmentName,
      deptDesc: description,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/departments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(departmentData),
      });

      if (response.ok) {
        const data = await response.json();
        setAlertMessage(`Department "${data.deptName}" has been successfully added!`);
        setAlertType('success');
        resetForm();
        fetchDepartments();
      } else {
        const error = await response.text();
        setAlertType('danger');
        setAlertMessage(error);
      }
    } catch (error) {
      setAlertType('danger');
      setAlertMessage('Error creating department. Please try again.');
    }
  };

  // Handle Delete
  // Handle Delete (Deactivate)
const handleDelete = async () => {
  if (!departmentCode) {
    setAlertType('danger');
    setAlertMessage('Please enter a Department Code to deactivate.');
    return;
  }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/departments/${departmentCode}`, {
        method: 'DELETE',
      });

    const data = await response.text(); // Get the response message (text format)

    if (response.ok) {
      setAlertMessage(data); // Show success message if the response is OK
      setAlertType('success');
      resetForm();
      fetchDepartments();
    } else {
      setAlertMessage(data); // Show the error message (e.g., not found or failure)
      setAlertType('danger');
    }
  } catch (error) {
    setAlertType('danger');
    setAlertMessage('Failed to deactivate department. Please try again.');
  }
};

  // Handle Edit
  const handleEdit = async () => {
    if (!departmentCode) {
      setAlertType('danger');
      setAlertMessage('Please enter a Department Code to edit.');
      return;
    }

    const updatedDepartmentData = {
      deptId: departmentCode,
      deptName: departmentName,
      deptDesc: description,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/departments/${departmentCode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedDepartmentData),
      });

      if (response.ok) {
        const data = await response.json();
        setAlertMessage(`Department "${data.deptName}" has been successfully updated!`);
        setAlertType('success');
        resetForm();
        fetchDepartments();
        setIsEditing(false);
      } else {
        throw new Error('Error updating department');
      }
    } catch (error) {
      setAlertType('danger');
      setAlertMessage('Error updating department. Please try again.');
    }
  };

  const handleSelectEdit = (department) => {
    setDepartmentCode(department.deptId);
    setDepartmentName(department.deptName);
    setDescription(department.deptDesc);
    setIsEditing(true);
  };

  const resetForm = () => {
    setDepartmentCode('');
    setDepartmentName('');
    setDescription('');
    setIsEditing(false);
  };

  const toggleTableVisibility = () => {
    setIsTableVisible(!isTableVisible);
    if (!isTableVisible) {
      fetchDepartments();
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  return (
    <Container className="d-flex justify-content-center" style={{ minHeight: '100vh', paddingTop: '20px' }}>
      <div className={`d-flex ${isTableVisible ? 'justify-content-start' : 'justify-content-center'}`} style={{ width: '100%' }}>
        {/* Form Container */}
        <div className="border p-4 rounded" style={{ maxWidth: '500px', width: '100%', height: '400px',marginTop:'69px' }}>
          <h3 className="text-center mb-4" style={{ fontSize: '30px', color: 'navy' }}>Manage Department</h3>
          
          {alertMessage && (
            <Alert variant={alertType} onClose={handleAlertClose} dismissible>
              {alertMessage}
            </Alert>
          )}
          
          <Form>
            {/* Department ID */}
            <Form.Group className="mb-3" controlId="departmentId">
              <Form.Label>Department ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Department ID"
                value={departmentCode}
                onChange={(e) => setDepartmentCode(e.target.value)}
                disabled={isEditing}
              />
            </Form.Group>

            {/* Department Name */}
            <Form.Group className="mb-3" controlId="departmentName">
              <Form.Label>Department Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Department Name"
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
              />
            </Form.Group>

            {/* Description */}
            <Form.Group className="mb-3" controlId="departmentDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ resize: 'none' }}
              />
            </Form.Group>

            {/* Buttons */}
            <Row className="justify-content-center">
              <Col className="d-flex justify-content-between">
                <Button variant="primary" style={{ backgroundColor: 'navy', borderColor: 'navy', width: '30%' }} onClick={isEditing ? handleEdit : handleCreate}>
                  {isEditing ? 'Update' : 'Create'}
                </Button>
                <Button variant="primary" style={{ backgroundColor: 'navy', borderColor: 'navy', width: '30%' }} onClick={handleDelete}>
                  Remove
                </Button>
                <Button variant="primary" style={{ backgroundColor: 'navy', borderColor: 'navy', color: 'white', width: '30%' }} onClick={toggleTableVisibility}>
                  {isTableVisible ? 'Hide' : 'View'}
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        {/* Department Table */}
        {isTableVisible && (
          
          <div style={{ maxHeight: '480px', overflowY: 'auto' }} className='ps-4'>
            <h4 className="text-center mb-3" style={{ color: 'navy' }}>Departments List</h4>
          <table className="table table-striped table-bordered table-hover rounded-5">
    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
         
                <tr>
                  <th className='bg-secondary'>Department Code</th>
                  <th className='bg-secondary'>Department Name</th>
                  <th className='bg-secondary'>Description</th>
                  <th className='bg-secondary'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {departments.map((department) => (
                  <tr key={department.deptId}>
                    <td>{department.deptId}</td>
                    <td>{department.deptName}</td>
                    <td>{department.deptDesc}</td>
                    <td>
                      <Button variant="secondary" size="sm" style={{color:'white',backgroundColor:'navy',width:'60px'}} onClick={() => handleSelectEdit(department)}>Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Container>
  );
};

export default DepartmentForm;
